// src/features/tidsregistering/components/attendance/AttendanceSidebarCell.jsx
import React, { memo } from 'react';
import { Box, Typography, CircularProgress, useTheme, alpha } from '@mui/material';
import StaffMember from '../../../../components/common/StaffMember';
import LightTooltip from '../../../../components/common/LightTooltip';
import { SEVERITY_ICON } from '../../constants/severity';
import { BALANCE_LABELS } from '../../constants/balances';

function hoursToDisplay(hours) {
  const h = Number(hours);
  if (Number.isNaN(h)) return '–';
  return `${h.toFixed(1)}t`;
}

function BalanceTooltipContent({ balances }) {
  if (balances === null) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5 }}>
        <CircularProgress size={12} />
        <Typography variant="caption" color="text.secondary">Henter saldi…</Typography>
      </Box>
    );
  }
  if (!balances || balances.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ p: 1.5, display: 'block' }}>
        Ingen saldi registreret
      </Typography>
    );
  }
  return (
    <Box sx={{ p: 1.5 }}>
      <Typography variant="subtitle2" sx={{ mb: 0.75 }}>Saldi</Typography>
      {balances.map((b) => (
        <Box key={b.absence_balance_id} sx={{ display: 'flex', justifyContent: 'space-between', gap: 3, py: 0.25 }}>
          <Typography variant="caption" color="text.secondary">
            {BALANCE_LABELS[b.balance_code] ?? b.balance_code}
          </Typography>
          <Typography variant="caption" fontWeight={600}>
            {hoursToDisplay(b.remaining_hours)}
            <Typography component="span" variant="caption" color="text.disabled" sx={{ ml: 0.5 }}>
              / {hoursToDisplay(b.entitlement_hours)}
            </Typography>
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

export const GROUP_HEADER_HEIGHT = 28;

function GroupHeaderCell({ category, categoryHeight }) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        height: categoryHeight,
        display: 'flex',
        alignItems: 'center',
        px: 1.5,
        bgcolor: alpha(theme.palette.primary.main, 0.08),
        borderBottom: `1px solid ${theme.palette.divider}`,
        borderTop: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Typography variant="caption" fontWeight={700} color="primary.main" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {category.name}
      </Typography>
    </Box>
  );
}

function AttendanceSidebarCell({ category, categoryHeight, balances, onFetchBalances }) {
  const theme = useTheme();
  if (category.isGroupHeader) {
    return <GroupHeaderCell category={category} categoryHeight={categoryHeight} />;
  }
  const { staffMember, deviation = {} } = category;
  const { deltaMinutes = 0, severity = 0 } = deviation;

  const deviationLabel =
    deltaMinutes === 0
      ? null
      : `${deltaMinutes > 0 ? '+' : ''}${deltaMinutes}m`;

  const handleMouseEnter = () => {
    if (onFetchBalances && balances === undefined) {
      onFetchBalances(category.id);
    }
  };

  return (
    <Box
      sx={{
        height: categoryHeight,
        display: 'flex',
        alignItems: 'stretch',
        borderBottom: `1px solid ${theme.palette.divider}`,
        pointerEvents: 'auto',
        overflow: 'hidden',
      }}
    >
      {/* Track labels */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-around',
          width: 44,
          flexShrink: 0,
          borderRight: `1px solid ${theme.palette.divider}`,
          px: 0.5,
        }}
      >
        <Typography variant="caption" sx={{ fontSize: 9, color: 'primary.main', fontWeight: 700, lineHeight: 1, textAlign: 'center' }}>
          Plan
        </Typography>
        <Typography variant="caption" sx={{ fontSize: 9, color: 'text.secondary', lineHeight: 1, textAlign: 'center' }}>
          Mødt
        </Typography>
      </Box>

      {/* Staff member info + deviation */}
      <LightTooltip
        title={<BalanceTooltipContent balances={balances} />}
        placement="right"
        arrow
        enterDelay={300}
        disableHoverListener={false}
      >
        <Box
          onMouseEnter={handleMouseEnter}
          sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1, px: 1, minWidth: 0, cursor: 'default' }}
        >
          <StaffMember
            staffMember={staffMember}
            avatarSize={28}
            gap={1}
            nameVariant="body2"
            nameSx={{ fontSize: 12, flex: 1, minWidth: 0 }}
            sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}
            clickable={false}
          />

          {severity > 0 && deviationLabel && (
            <LightTooltip title={deviationLabel} placement="top" arrow>
              <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                {SEVERITY_ICON[severity] ?? null}
              </Box>
            </LightTooltip>
          )}
        </Box>
      </LightTooltip>
    </Box>
  );
}

export default memo(AttendanceSidebarCell);
